// src/pages/OPDAccessibilityPage.js
import React, { useState, useEffect, useRef } from "react";
import { 
  Accessibility, 
  Moon, 
  Sun, 
  Type, 
  Eye, 
  AlignLeft, 
  RotateCcw, 
  X, 
  Move,
  Settings,
  Palette,
  CheckCircle,
  Monitor
} from 'lucide-react';

export default function OPDAccessibilityPage() {
  const [theme, setTheme] = useState("light"); // light / dark
  const [fontSize, setFontSize] = useState("medium"); // small / medium / large
  const [highContrast, setHighContrast] = useState(false);
  const [lineHeight, setLineHeight] = useState("normal"); // normal / wide
  const [panelOpen, setPanelOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  const panelRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Load saved settings from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("opd-theme");
    const savedFontSize = localStorage.getItem("opd-font-size");
    const savedHighContrast = localStorage.getItem("opd-high-contrast");
    const savedLineHeight = localStorage.getItem("opd-line-height");

    if (savedTheme) setTheme(savedTheme);
    if (savedFontSize) setFontSize(savedFontSize);
    if (savedHighContrast) setHighContrast(savedHighContrast === "true");
    if (savedLineHeight) setLineHeight(savedLineHeight);
  }, []);

  // Apply settings to entire page
  useEffect(() => {
    const html = document.documentElement;

    html.setAttribute("data-theme", theme);
    html.style.fontSize =
      fontSize === "small" ? "14px" : fontSize === "medium" ? "16px" : "18px";
    html.style.lineHeight = lineHeight === "normal" ? "1.5" : "2";
    html.style.filter = highContrast ? "contrast(150%)" : "contrast(100%)";

    localStorage.setItem("opd-theme", theme);
    localStorage.setItem("opd-font-size", fontSize);
    localStorage.setItem("opd-high-contrast", highContrast);
    localStorage.setItem("opd-line-height", lineHeight);
  }, [theme, fontSize, highContrast, lineHeight]);

  const showSuccessNotification = () => {
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const resetDefaults = () => {
    setTheme("light");
    setFontSize("medium");
    setHighContrast(false);
    setLineHeight("normal");
    showSuccessNotification();
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    setDragging(true);
    const rect = panelRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (dragging && panelRef.current) {
      panelRef.current.style.left = `${e.clientX - offset.x}px`;
      panelRef.current.style.top = `${e.clientY - offset.y}px`;
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  return (
    <div className="min-h-screen bg-gray-100 transition-colors duration-300 p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3">
          <Accessibility className="w-8 h-8 text-teal-600" />
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Accessibility Center</h1>
            <p className="text-gray-500">Customize your viewing experience for better accessibility</p>
          </div>
        </div>
      </header>

      {/* Success Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-[60] bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 shadow-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-green-800 font-semibold">Settings Reset</p>
            <p className="text-green-700 text-sm">All accessibility settings have been restored to default</p>
          </div>
        </div>
      )}

      {/* Live Preview Content */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
          <div className="flex items-center gap-3">
            <Monitor className="w-6 h-6" />
            <div>
              <h2 className="text-2xl font-bold">Live Preview</h2>
              <p className="text-teal-100 mt-1">See how your accessibility settings affect the content</p>
            </div>
          </div>
        </div>

        <div
          className="p-6 transition-colors duration-300"
          style={{
            backgroundColor: theme === "dark" ? "#1e1e1e" : "#ffffff",
            color: theme === "dark" ? "#e0e0e0" : "#111827",
          }}
        >
          <h3 className="text-3xl font-bold mb-4">Welcome to Our Website</h3>
          <p className="mb-4">
            This is a sample web page to demonstrate accessibility settings. Adjust the panel to change
            font size, enable dark mode, high contrast, or modify line spacing for better readability.
          </p>
          
          <h4 className="text-2xl font-semibold mb-2">Our Services</h4>
          <p className="mb-2">
            We offer inclusive solutions for everyone. Our goal is to make web content accessible and readable
            for users with different needs and preferences.
          </p>
          
          <h5 className="text-xl font-bold mb-2">Get Started</h5>
          <p className="mb-4">
            Click the accessibility button to explore customization options that can improve your browsing experience.
          </p>
          
          <button 
            className="px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all shadow-lg"
          >
            Learn More About Accessibility
          </button>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg" style={{
              backgroundColor: theme === "dark" ? "#2a2a2a" : "#f9fafb",
            }}>
              <h6 className="font-semibold mb-2">Visual Accessibility</h6>
              <p className="text-sm">Adjust colors, contrast, and text size for better visibility.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" style={{
              backgroundColor: theme === "dark" ? "#2a2a2a" : "#f9fafb",
            }}>
              <h6 className="font-semibold mb-2">Reading Comfort</h6>
              <p className="text-sm">Modify line spacing and font sizes for comfortable reading.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Floating Accessibility Button */}
      <button
        onClick={() => setPanelOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 rounded-full shadow-xl hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-300 z-50 transition-all transform hover:scale-110"
        aria-label="Open Accessibility Settings"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* Enhanced Accessibility Panel */}
      {panelOpen && (
        <aside
          ref={panelRef}
          className="fixed top-20 right-4 w-80 bg-white shadow-2xl border border-gray-200 rounded-xl z-50 overflow-hidden"
        >
          {/* Panel Header */}
          <div 
            onMouseDown={handleMouseDown}
            className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 cursor-move select-none"
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                <h2 className="text-lg font-bold">Accessibility Settings</h2>
              </div>
              <div className="flex items-center gap-2">
                <Move className="w-4 h-4 opacity-70" />
                <button
                  onClick={() => setPanelOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Close Accessibility Panel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="text-teal-100 text-sm mt-1">Customize your viewing experience</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Theme Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-800">Theme</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {theme === "light" ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-blue-500" />}
                  <span className="text-sm text-gray-600">
                    {theme === "light" ? "Light Mode" : "Dark Mode"}
                  </span>
                </div>
                <button
                  onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                  className={`w-14 h-7 rounded-full relative transition-colors ${
                    theme === "dark" ? "bg-teal-600" : "bg-gray-300"
                  }`}
                  aria-label="Toggle Dark Mode"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transform transition-transform flex items-center justify-center ${
                      theme === "dark" ? "translate-x-7" : ""
                    }`}
                  >
                    {theme === "light" ? <Sun className="w-3 h-3 text-yellow-500" /> : <Moon className="w-3 h-3 text-blue-500" />}
                  </span>
                </button>
              </div>
            </div>

            {/* Font Size Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Type className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-800">Font Size</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "small", label: "Small", size: "text-sm" },
                  { key: "medium", label: "Medium", size: "text-base" },
                  { key: "large", label: "Large", size: "text-lg" }
                ].map((size) => (
                  <button
                    key={size.key}
                    onClick={() => setFontSize(size.key)}
                    className={`px-3 py-2 rounded-lg border transition-all ${
                      fontSize === size.key
                        ? "bg-teal-600 text-white border-teal-600 shadow-md"
                        : "bg-white text-gray-700 border-gray-300 hover:border-teal-300"
                    }`}
                  >
                    <span className={size.size}>{size.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* High Contrast Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-800">High Contrast</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {highContrast ? "Enhanced contrast enabled" : "Normal contrast"}
                  </span>
                </div>
                <button
                  onClick={() => setHighContrast(!highContrast)}
                  className={`w-14 h-7 rounded-full relative transition-colors ${
                    highContrast ? "bg-yellow-500" : "bg-gray-300"
                  }`}
                  aria-label="Toggle High Contrast"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transform transition-transform flex items-center justify-center ${
                      highContrast ? "translate-x-7" : ""
                    }`}
                  >
                    <Eye className="w-3 h-3 text-gray-600" />
                  </span>
                </button>
              </div>
            </div>

            {/* Line Height Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlignLeft className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-800">Line Spacing</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "normal", label: "Normal" },
                  { key: "wide", label: "Wide" }
                ].map((lh) => (
                  <button
                    key={lh.key}
                    onClick={() => setLineHeight(lh.key)}
                    className={`px-3 py-2 rounded-lg border transition-all ${
                      lineHeight === lh.key
                        ? "bg-teal-600 text-white border-teal-600 shadow-md"
                        : "bg-white text-gray-700 border-gray-300 hover:border-teal-300"
                    }`}
                  >
                    {lh.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reset Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={resetDefaults}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Default
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Enhanced Global Dark Mode CSS */}
      <style>{`
        html[data-theme="dark"] {
          background-color: #0f172a;
          color: #e2e8f0;
        }
        html[data-theme="dark"] .bg-gray-100 {
          background-color: #1e293b !important;
        }
        html[data-theme="dark"] .bg-white {
          background-color: #334155 !important;
        }
        html[data-theme="dark"] .text-gray-800 {
          color: #e2e8f0 !important;
        }
        html[data-theme="dark"] .text-gray-500 {
          color: #94a3b8 !important;
        }
        html[data-theme="dark"] .text-gray-600 {
          color: #cbd5e1 !important;
        }
        html[data-theme="dark"] .text-gray-700 {
          color: #e2e8f0 !important;
        }
        html[data-theme="dark"] .border-gray-200 {
          border-color: #475569 !important;
        }
        html[data-theme="dark"] .border-gray-300 {
          border-color: #64748b !important;
        }
        html[data-theme="dark"] a {
          color: #38bdf8;
        }
        html[data-theme="dark"] img {
          filter: brightness(0.8);
        }
        html[data-theme="dark"] .shadow-md {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2);
        }
        html[data-theme="dark"] .shadow-xl {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
        }
        html[data-theme="dark"] .shadow-2xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
        }
      `}</style>
    </div>
  );
}
